/**
 * purchaseRegister.ts — Pure aggregation of a period's purchases into a
 * register: every supplier bill with its taxable value and GST split, plus
 * per-supplier totals. The books-side companion to the sales figures, and the
 * source a shopkeeper (or their CA) checks input-tax credit against.
 *
 * One thing it flags: purchases from suppliers with **no GSTIN** carry no
 * claimable ITC, so their value is surfaced separately rather than silently
 * inflating the credit a user might expect.
 *
 * Pure — the screen feeds it rows from `loadBooksPurchases`.
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface PurchaseInput {
  supplierName: string;
  supplierGstin: string | null;
  invoiceNumber: string;
  date: Date;
  /** Taxable value (ex-GST). */
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totalTax: number;
  /** Invoice total (incl. GST). */
  value: number;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface PurchaseRow extends PurchaseInput {
  /** Supplier has no GSTIN — no input-tax credit is claimable on this bill. */
  noItc: boolean;
}

export interface SupplierPurchaseSummary {
  supplierName: string;
  gstin: string | null;
  count: number;
  taxable: number;
  totalTax: number;
  value: number;
}

export interface PurchaseTotals {
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totalTax: number;
  value: number;
  count: number;
}

export interface PurchaseRegister {
  /** Every purchase, oldest first. */
  rows: PurchaseRow[];
  totals: PurchaseTotals;
  /** Per supplier, largest spend first. */
  bySupplier: SupplierPurchaseSummary[];
  /** Tax that is claimable as ITC (from GSTIN-bearing suppliers only). */
  claimableItc: number;
  /** Count and value of purchases with no claimable ITC. */
  noItcCount: number;
  noItcValue: number;
}

export function buildPurchaseRegister(purchases: PurchaseInput[]): PurchaseRegister {
  const rows: PurchaseRow[] = purchases
    .map((p) => ({ ...p, noItc: !p.supplierGstin?.trim() }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const totals: PurchaseTotals = {
    taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, totalTax: 0, value: 0, count: rows.length,
  };
  let claimableItc = 0;
  let noItcCount = 0;
  let noItcValue = 0;

  const supMap = new Map<string, { name: string; gstin: string | null; count: number; taxable: number; totalTax: number; value: number }>();

  for (const r of rows) {
    totals.taxable += r.taxable;
    totals.cgst += r.cgst;
    totals.sgst += r.sgst;
    totals.igst += r.igst;
    totals.cess += r.cess;
    totals.totalTax += r.totalTax;
    totals.value += r.value;

    if (r.noItc) {
      noItcCount += 1;
      noItcValue += r.value;
    } else {
      claimableItc += r.totalTax;
    }

    // Group by supplier name + GSTIN so two suppliers with the same name split.
    const key = `${r.supplierName} ${r.supplierGstin ?? ''}`;
    const acc = supMap.get(key) ?? { name: r.supplierName, gstin: r.supplierGstin, count: 0, taxable: 0, totalTax: 0, value: 0 };
    acc.count += 1;
    acc.taxable += r.taxable;
    acc.totalTax += r.totalTax;
    acc.value += r.value;
    supMap.set(key, acc);
  }

  const bySupplier: SupplierPurchaseSummary[] = [...supMap.values()]
    .map((s) => ({
      supplierName: s.name,
      gstin: s.gstin,
      count: s.count,
      taxable: round2(s.taxable),
      totalTax: round2(s.totalTax),
      value: round2(s.value),
    }))
    .sort((a, b) => b.value - a.value);

  return {
    rows,
    totals: {
      taxable: round2(totals.taxable),
      cgst: round2(totals.cgst),
      sgst: round2(totals.sgst),
      igst: round2(totals.igst),
      cess: round2(totals.cess),
      totalTax: round2(totals.totalTax),
      value: round2(totals.value),
      count: totals.count,
    },
    bySupplier,
    claimableItc: round2(claimableItc),
    noItcCount,
    noItcValue: round2(noItcValue),
  };
}
